import { useEffect, useRef } from 'react';
import {
  dragAndDropFeature,
  hotkeysCoreFeature,
  renamingFeature,
  syncDataLoaderFeature,
  TreeConfig,
  TreeInstance,
} from '@headless-tree/core';
import { useTree } from '@headless-tree/react';
import { getItemName, handleDrop, handleRename, isItemFolder } from './helpers';
import { FileItemType, TreeItemsType } from './types';

export type TreePropsType<T extends FileItemType> = {
  files: TreeItemsType<T>;
  onRenameItem: (params: { fileId: string; newName: string }) => void;
  onMoveItem: (params: { fileId: string; newParentId: string }) => void;
} & Partial<TreeConfig<T>>;

export const useFileTree = <T extends FileItemType>({
  files,
  onRenameItem,
  onMoveItem,
  ...treeConfig
}: TreePropsType<T>): TreeInstance<T> => {
  const ref = useRef<TreeItemsType<T>>(files);

  const dataLoader = {
    getItem: (id: string) => ref.current[id],
    getChildren: (id: string) => ref.current[id]?.children ?? [],
  };

  const tree = useTree<T>({
    rootItemId: 'root',
    getItemName,
    isItemFolder,
    dataLoader,
    onRename: handleRename<T>(onRenameItem),
    indent: 20,
    onDrop: handleDrop<T>(onMoveItem),
    features: [
      syncDataLoaderFeature,
      renamingFeature,
      hotkeysCoreFeature,
      dragAndDropFeature,
    ],
    ...treeConfig,
  });

  useEffect(() => {
    ref.current = files;
    tree.rebuildTree();
  }, [files, tree]);

  return tree;
};
